/**
 * RobotStateChip.jsx
 * Shows robot motion state chip + connection status chip side by side.
 */
import React from "react";
import { Chip, Stack } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PropTypes from "prop-types";
import { tokens } from "../../theme";

const getStateConfig = (state) => {
  if (state === "MOVING")
    return {
      color: tokens.accent2,
      bg: tokens.accentSoft,
      border: tokens.accent,
      dot: tokens.accent,
    };
  if (state === "STUCK")
    return {
      color: tokens.danger,
      bg: tokens.dangerSoft,
      border: tokens.danger,
      dot: tokens.danger,
    };
  if (state === "REACHED")
    return {
      color: tokens.success,
      bg: tokens.successSoft,
      border: tokens.success,
      dot: tokens.success,
    };
  return {
    color: tokens.warning,
    bg: tokens.warningSoft,
    border: tokens.warning,
    dot: tokens.warning,
  };
};

// Boxed-in states read better in words than as raw enum values.
const STATE_LABELS = { BOXED_IN: "PATH BLOCKED", STUCK: "STUCK" };

const RobotStateChip = ({ state, isRobotConnected, hasSelectedRobot = true }) => {
  const cfg = getStateConfig(state);
  return (
    <Stack direction="row" gap={1} alignItems="center">
      <Chip
        icon={
          <FiberManualRecordIcon
            sx={{ fontSize: "9px !important", color: `${cfg.dot} !important` }}
          />
        }
        label={STATE_LABELS[state] ?? state}
        size="small"
        sx={{
          height: 24,
          fontSize: 11,
          fontWeight: 700,
          borderRadius: "20px",
          color: cfg.color,
          background: cfg.bg,
          border: `1px solid ${cfg.border}`,
        }}
      />
      {/* Connection status only shown when a robot is selected in the top bar */}
      {hasSelectedRobot && (
        <Chip
          icon={
            <FiberManualRecordIcon
              sx={{
                fontSize: "9px !important",
                color: `${isRobotConnected ? tokens.success : tokens.danger} !important`,
              }}
            />
          }
          label={isRobotConnected ? "Connected" : "Disconnected"}
          size="small"
          sx={{
            height: 24,
            fontSize: 11,
            fontWeight: 700,
            borderRadius: "20px",
            color: isRobotConnected ? tokens.success : tokens.danger,
            background: isRobotConnected ? tokens.successSoft : tokens.dangerSoft,
            border: `1px solid ${isRobotConnected ? tokens.success : tokens.danger}`,
          }}
        />
      )}
    </Stack>
  );
};

RobotStateChip.propTypes = {
  state: PropTypes.string,
  isRobotConnected: PropTypes.bool,
  hasSelectedRobot: PropTypes.bool,
};

export default RobotStateChip;
